/**
 * The Enabled switch and the status the form's values would give the campaign.
 * Turning the switch off and saving is how a campaign is disabled. While the
 * schedule is out of order there is no honest status to show, so the card
 * just asks for the schedule to be fixed.
 */
import { campaignStatus, TicketSalesCampaignStatus } from '@bonsai/shared';

import { Switch } from '../../../shared/Switch';

import { formSchedule, type CampaignFormValues } from './campaign-form';
import { CampaignStatusLine } from './CampaignStatusLine';

export interface CampaignStatusCardProps {
  values: CampaignFormValues;
  now: Date;
  onEnabledChange: (enabled: boolean) => void;
}

export function CampaignStatusCard({ values, now, onEnabledChange }: CampaignStatusCardProps) {
  const schedule = formSchedule(values);
  const ended =
    schedule !== null && campaignStatus(schedule, now) === TicketSalesCampaignStatus.ENDED;
  return (
    <section
      className="nb-card"
      aria-label="Campaign status"
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>
            {values.enabled ? 'Enabled' : 'Disabled'}
          </div>
          <div
            id="campaign-enabled-hint"
            style={{ fontSize: 12, color: 'var(--nb-subtle)', marginTop: 2 }}
          >
            {values.enabled
              ? 'Customers get this discount whenever the campaign is running.'
              : 'Customers won’t see this discount, even during the scheduled dates.'}
          </div>
        </div>
        <Switch
          checked={values.enabled}
          onChange={onEnabledChange}
          aria-label="Enabled"
          aria-describedby="campaign-enabled-hint"
        />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          paddingTop: 14,
          borderTop: '1px solid var(--nb-border)',
        }}
      >
        <CampaignStatusLine schedule={schedule} now={now} />
      </div>
      {ended && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--nb-muted)',
            background: 'var(--nb-surface)',
            borderRadius: 8,
            padding: '9px 11px',
          }}
        >
          This campaign has ended. Move the end date forward to run it again.
        </div>
      )}
    </section>
  );
}
